import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { asLiteral, literalOf } from "@/utils/formula";

type LiteralInputProps = {
    id: string;
    /** The stored value, escape included: this component is what hides that escape. */
    value: unknown;
    /** A field the server declared large writes into a box rather than onto a line. */
    large?: boolean;
    required?: boolean;
    disabled?: boolean;
    onChange: (value: string) => void;
};

/**
 * The value half of a switchable field, with the mark's escape kept out of sight.
 *
 * A switchable field stores `==8 la conclusion` for the value `=8 la conclusion`, because a
 * single `=` is what says « this is an expression » and a literal that really opens with one
 * has to be able to exist. That escape is a storage convention and the author never asked
 * for it, so it is written on the way in and stripped on the way out here — one place, at
 * the seam, rather than in the ten inputs downstream.
 *
 * It also settles what typing `=` as the first character means. Registered straight into the
 * form, that keystroke would store a marked value and the field would flip into an
 * expression under the caret, swapping the input mid-word: the conversion is a button, and
 * pressing a key is not pressing it.
 *
 * Two shapes and no more, which is not a shortcut: the fields the server declares switchable
 * are the free-text ones. A closed choice, a delay and a port name are refused the button by
 * decision, so there is no third input this would have to grow.
 */
export function LiteralInput({ id, value, large, required, disabled, onChange }: LiteralInputProps) {
    const shown = literalOf(value);
    const text = shown === null || shown === undefined ? "" : String(shown);
    const write = (next: string) => onChange(asLiteral(next) as string);

    if (large) {
        return (
            <Textarea
                id={id}
                name={id}
                value={text}
                required={required}
                disabled={disabled}
                onChange={event => write(event.target.value)}
                className="rounded border px-2 py-1"
            />
        );
    }

    return <Input id={id} name={id} type="text" value={text} required={required} disabled={disabled} onChange={event => write(event.target.value)} />;
}
